import { unread } from '@/lib/inbox/items'
import type { InboxReadMarks, InboxThread } from '@/types/inbox'

/** The most notices shown at once; the rest fold into one count. */
export const NOTICE_VISIBLE_CAP = 3

/** How long a notice that does not need action stays before it leaves. */
export const FOR_YOU_NOTICE_MS = 6000

/** The route names of the Inbox, where the list shows new threads itself and no notice floats. */
export const INBOX_ROUTE_NAMES: readonly string[] = ['inbox', 'inbox-sent', 'inbox-archive']

/** The newest message time, in ms, of each thread already shown, put aside or left; a thread cards again only past it. */
export type SettledNotices = ReadonlyMap<string, number>

/** The notices split into the ones shown and how many are folded away. */
export interface NoticeStack {
  shown: readonly InboxThread[]
  folded: number
}

/** The threads that earn a notice: newer than the watermark and the settled mark, unread, important first then newest. */
export function pendingNotices(
  threads: readonly InboxThread[],
  readMarks: InboxReadMarks,
  watermark: Date,
  settled: SettledNotices,
): InboxThread[] {
  return threads
    .filter(
      (thread) =>
        thread.lastEntryAt.getTime() > watermark.getTime() &&
        thread.lastEntryAt.getTime() > (settled.get(thread.id) ?? Number.NEGATIVE_INFINITY) &&
        unread(thread, readMarks),
    )
    .sort(
      (a, b) =>
        Number(b.actionNeeded) - Number(a.actionNeeded) ||
        b.lastEntryAt.getTime() - a.lastEntryAt.getTime(),
    )
}

/** The settled marks with the given threads added at their newest message time. */
export function settleNotices(
  settled: SettledNotices,
  threads: readonly InboxThread[],
): SettledNotices {
  const next = new Map(settled)
  for (const thread of threads) next.set(thread.id, thread.lastEntryAt.getTime())
  return next
}

/** The first `cap` notices and the count of the rest. */
export function stackNotices(pending: readonly InboxThread[], cap: number): NoticeStack {
  return { shown: pending.slice(0, cap), folded: Math.max(0, pending.length - cap) }
}
